import { Select, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import type { ContractTerminationOptions } from '@weft/api-definition';
import { CONTRACT_TERMINATION_OPTIONS } from '@weft/api-definition';
import { raiseUnreachable } from '@weft/utils';
import type { BaseOptionType } from 'antd/es/select';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React, { useState } from 'react';
import { useTerminateContractMutation } from '../../../../../hooks/requests/use-terminate-contract';
import { useDialog } from '../../../../../hooks/use-dialog';

type MenuItemTerminateContractProps = { 
  customerName: string;
  contractId: string;
  isActive: boolean;
};

export const MenuItemTerminateContract = (
  { customerName, contractId, isActive }: MenuItemTerminateContractProps,
) => {
  const { handleClose, handleOpen, isDialogOpen } = useDialog();
  const [selectedTerminationOption, setSelectedTerminationOption] = useState<ContractTerminationOptions>(CONTRACT_TERMINATION_OPTIONS.Immediate);
  const terminateContractMutation = useTerminateContractMutation(contractId);

  const handleDialogClose = () => {
    if (!terminateContractMutation.isLoading) {
      handleClose();
    }
  };

  const handleDialogSubmit = async () => { 
    await runMutation(
      terminateContractMutation,
      { terminationOption: selectedTerminationOption },
      {
        pending: 'Terminating...',
        success: 'Terminated',
        error: 'Failed to terminate contract',
      },
      {
        onSuccess: () => {
          handleClose();
        },
      },
    );
  };

  return (
    <div>
      <MenuItem
        onClick={handleOpen}
        disabled={!isActive}
      >
        <Typography>Terminate</Typography>
      </MenuItem>
      <VyDialog 
        open={isDialogOpen}
        onClose={handleDialogClose}
        label={`You are about to terminate ${customerName}'s contract`}
        labelDescription="This will terminate the contract and scheduled invoices"
        cancelButtonText="Cancel"
        ctaText="Confirm"
        ctaDisabled={terminateContractMutation.isLoading}
        onSubmit={handleDialogSubmit}
      >
        <Typography
          variant="h6"
          className='mb-2'
          style={{ fontSize: '12px' }}
        >
          Termination date
        </Typography>
        <Select
          disabled={terminateContractMutation.isLoading}
          fullWidth
          size='small'
          value={selectedTerminationOption}
          onChange={(event) => setSelectedTerminationOption(event.target.value as ContractTerminationOptions)}
        >
          {getTerminationOptionSelectOptions().map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </VyDialog>
    </div>
  );
};

function getTerminationOptionSelectOptions() {
  return Object.values(CONTRACT_TERMINATION_OPTIONS).map<BaseOptionType>((option) => ({
    value: option,
    label: getTerminationOptionLabel(option),
  }));
}

function getTerminationOptionLabel(terminationOption: ContractTerminationOptions) { 
  switch (terminationOption) {
    case CONTRACT_TERMINATION_OPTIONS.Immediate:
      return 'Immediate';
    case CONTRACT_TERMINATION_OPTIONS.EndOfBillingCycle:
      return 'End of this billing cycle';
    default:
      return raiseUnreachable(terminationOption);
  }
}